import { useMemo, useRef, useState } from 'react'
import useClickOutsideAndEscape from 'hooks/useClickOutsideAndEscape'
import { CheckBoxGroup } from '..'
import DropdownButton from './dropdownButton'
import { MultiselectDropdownInput } from 'types/input'
import { FieldValues } from 'react-hook-form'

/**
 *
 * Multi Select Dropdown element
 * -
 * Need To Pass Following Params
 * - `name` _required_
 * - `options` _required_
 * - `setValue` _required_
 * - `error` _optional_
 * - `parentClassName` _optional_
 * - `className` _Optional_
 * - `hintText` _optional_
 * - `label` _Optional_
 * - `defaultSelected` _Optional_
 * - `includeSelectAll` _Optional_
 * - `searchable` _Optional_ - Enable search functionality
 *
 */
const MultiSelectDropdown = <T extends FieldValues>({
  parentClassName = '',
  className = '',
  label,
  name,
  setValue,
  error,
  hintText,
  options,
  defaultSelected,
  includeSelectAll,
  required,
  reset,
  searchable = true,
  disabled = false,
}: MultiselectDropdownInput<T> & { searchable?: boolean }) => {
  // To toggle the dropdown visibility
  const [isVisible, setIsVisible] = useState(false)

  // To store total selected values
  const [totalSelected, setTotalSelected] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  // To close the dropdown in outside click
  const ref = useRef<HTMLDivElement | null>(null)
  useClickOutsideAndEscape({
    ref,
    callback: () => {
      setIsVisible(false)
      setSearchQuery('')
    },
  })

  // Filter options based on search query - searches both label and value
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options
    }

    const lowerCaseQuery = searchQuery.toLowerCase()

    return options.filter((option) => {
      const label = option.label.toLowerCase()
      const value = String(option.value).toLowerCase()

      return label.includes(lowerCaseQuery) || value.includes(lowerCaseQuery)
    })
  }, [options, searchQuery, searchable])

  // Focus search input when dropdown opens
  useMemo(() => {
    if (isVisible && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isVisible, searchable])

  //To handle rendering of Button or Checkbox in the dropdown
  const renderInput = useMemo(() => {
    if (filteredOptions.length === 0) {
      return (
        <div className="px-4 py-2.5 text-left text-gray-400">
          No results found
        </div>
      )
    }

    return (
      <CheckBoxGroup
        setValue={setValue}
        options={filteredOptions}
        name={name}
        setTotalSelected={setTotalSelected}
        includeSelectAll={includeSelectAll}
        parentClassName="px-2 max-w-[267px] w-full"
        defaultSelected={defaultSelected}
        reset={reset}
      />
    )
  }, [filteredOptions, reset, searchQuery])

  return (
    <div className={`relative w-full outline-none ${parentClassName}`}>
      {/* Label */}
      <label
        htmlFor={name}
        className="text-xs font-semibold text-sidebar-primary"
      >
        {label}
        {required && <span className="text-error">*</span>}
      </label>

      {/* Select Button */}
      <div ref={ref}>
        <DropdownButton
          error={error}
          className={className}
          setIsVisible={setIsVisible}
          isVisible={isVisible}
          disabled={disabled}
        >
          {`${totalSelected} Selected`}{' '}
        </DropdownButton>

        {/* Options */}
        <div
          className={`absolute z-10 h-max max-h-64 w-full min-w-max origin-top overflow-auto rounded-md bg-white shadow-lg transition-all duration-200 ${
            isVisible ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
          }`}
        >
          {/* Search Input */}
          {searchable && (
            <div className="sticky top-0 z-20 border-b bg-white p-2">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-gray-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options List */}
          <div className="p-2">{renderInput}</div>
        </div>
      </div>

      {/* Error & Hint Text */}
      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
